/**
 * ⚔️ The Manual Battle Page — Feb 2026
 *
 * Visual combat layer (added Feb 2026):
 *   • Attack animations — attacker lunges, target shakes/flashes
 *   • Floating damage / heal / absorb numbers
 *   • Status-chip bar (🩸 Haemorrhage · ✋ Heal Block · 🪭 Soul Eater Drain …)
 *   • Rich combat-log highlighting by event (crit / true / heal / drain …)
 *
 * Combat state is still driven entirely by the backend
 * (manual_battle_service + status_engine). The frontend is a pure
 * presentation layer that diffs `battle.log` for new entries and
 * derives a transient `effect` to play.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import HighlightReelModal from "@/components/HighlightReelModal";
import {
  SpriteFighter,
  SpriteStyles,
  animClassFor,
  flashClassFor,
} from "@/components/battle/SpriteFighter";
import ResourceLegend from "@/components/battle/ResourceLegend";
import BattleBagButton from "@/components/combat/BattleBagButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EFFECT_DURATION_MS = 900;


export default function BattlePage() {
  const { isAuthed } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = new URLSearchParams(location.search).get("return_to");
  const [battle, setBattle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showReel, setShowReel] = useState(false);

  // 🎞️ Transient combat effect derived from the latest log entry.
  //   shape: { key, attacker, target, vfx[], damage, healed, absorbed }
  const [effect, setEffect] = useState(null);
  const lastLogLen = useRef(0);
  const effectChain = useRef([]);      // queue of pending effects
  const effectTimer = useRef(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/manual-battle/state`);
      setBattle(r.data.battle);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Couldn't load battle state.");
    }
  };

  useEffect(() => {
    if (isAuthed) refresh();
  }, [isAuthed]);

  // 🎞️ Walk through ALL newly-appended log entries and play each effect
  // sequentially (player move animation → enemy counter animation). The
  // backend appends multiple entries per round, so chaining is required —
  // otherwise the player's attack animation is swallowed by the enemy's
  // immediate counter (see iteration_62.json RCA).
  const playNext = () => {
    const queue = effectChain.current;
    if (queue.length === 0) {
      setEffect(null);
      effectTimer.current = null;
      return;
    }
    const next = queue.shift();
    setEffect(next);
    effectTimer.current = setTimeout(playNext, EFFECT_DURATION_MS);
  };

  useEffect(() => {
    const log = battle?.log || [];
    // If the log shrunk (new battle started / refresh wiped state), reset
    // the pointer so we don't skip entries on the next round.
    if (log.length < lastLogLen.current) {
      lastLogLen.current = 0;
      effectChain.current = [];
      if (effectTimer.current) {
        clearTimeout(effectTimer.current);
        effectTimer.current = null;
      }
      setEffect(null);
    }
    if (log.length > lastLogLen.current) {
      const newSlice = log.slice(lastLogLen.current);
      const derivedList = [];
      newSlice.forEach((entry, idx) => {
        const d = deriveEffectFromLogEntry(entry);
        if (d) derivedList.push({ ...d, key: `${lastLogLen.current + idx}-${entry.skill_id || "sys"}` });
      });
      if (derivedList.length > 0) {
        // Append to chain (don't clobber an in-flight one) and kick the
        // scheduler if idle.
        effectChain.current.push(...derivedList);
        if (!effectTimer.current) playNext();
      }
    }
    lastLogLen.current = log.length;
  }, [battle?.log?.length]);

  useEffect(() => () => {
    if (effectTimer.current) clearTimeout(effectTimer.current);
  }, []);

  const onStart = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/manual-battle/start`, { opponent_kind: "training_dummy" });
      setBattle(r.data);
      lastLogLen.current = (r.data?.log || []).length;
      toast.success("⚔️ The fight begins!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the battle.");
    } finally { setBusy(false); }
  };

  const onFireCombo = async (comboId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/manual-battle/combo-ult`, { combo_id: comboId });
      setBattle(r.data);
      toast.success("🤝 Combo Ultimate fired!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Combo rejected.");
    } finally { setBusy(false); }
  };

  const onSubmitMove = async (skillId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/manual-battle/submit-move`, { skill_id: skillId });
      setBattle(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move rejected.");
    } finally { setBusy(false); }
  };

  // 🎒 Battle Bag — use an item on the player's turn (consumes the turn).
  const onUseItem = async (consumableId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/manual-battle/use-item`, { consumable_id: consumableId });
      setBattle(r.data);
      toast.success("🎒 Item used!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Item use failed.");
      throw e;
    } finally { setBusy(false); }
  };

  const onForfeit = async () => {
    if (!confirm("Withdraw from the fight?")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/manual-battle/forfeit`);
      if (r.data?.battle) setBattle(r.data.battle);
      else await refresh();
      toast.message("🏳️ Withdrew from the fight.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't forfeit.");
    } finally { setBusy(false); }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a0010] via-black to-[#001a10] text-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="text-6xl mb-3">⚔️</div>
          <h1 className="text-4xl font-black text-amber-300" style={{ fontFamily: "Anton, sans-serif" }}>
            THE WILDS
          </h1>
          <p className="text-amber-100/70 mt-3">Sign in to take up arms.</p>
        </main>
        <Footer />
      </div>
    );
  }

  const isOver = battle?.is_over;
  const isPlayerTurn = battle?.is_player_turn;

  // Animation roles per side (attacker / target) — drives transforms.
  const playerRole = effect ? roleForSide(effect, "player") : null;
  const enemyRole = effect ? roleForSide(effect, "enemy") : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a0010] via-black to-[#001a10] text-white">
      <Header />
      <SpriteStyles />
      <main className="max-w-4xl mx-auto px-4 py-10 pb-32" data-testid="battle-page">
        <div className="text-center mb-6">
          <div className="text-5xl mb-1">⚔️</div>
          <h1 className="text-4xl font-black text-amber-300" style={{ fontFamily: "Anton, sans-serif" }}>
            THE WILDS
          </h1>
          <p className="text-[11px] text-amber-200/60 italic mt-1">
            Manual turn-based combat. Pick a skill. Read the fight.
          </p>
        </div>

        {!battle && (
          <div className="rounded-2xl border-2 border-amber-700/50 bg-black/50 p-8 text-center" data-testid="battle-idle">
            <div className="text-6xl mb-3">🎯</div>
            <h2 className="text-2xl font-black text-amber-200 mb-1">No active battle</h2>
            <p className="text-sm text-amber-100/70 mb-5">
              Square off against a Training Dummy to test your kit.
            </p>
            <button
              onClick={onStart}
              disabled={busy}
              data-testid="battle-start-btn"
              className="px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black"
            >
              ⚔️ START SPARRING
            </button>
          </div>
        )}

        {battle && (
          <>
            {/* Enemy card */}
            <CombatantCard
              actor={battle.enemy}
              side="enemy"
              isTurn={battle.active_side === "enemy" && !isOver}
              statuses={battle.enemy_statuses || []}
              role={enemyRole}
              effect={effect}
            />

            {/* 🎨 Hero Resource Guide (collapsible informational card) */}
            <ResourceLegend />

            {/* Combat log */}
            <div className="my-4 rounded-xl border border-purple-700/40 bg-black/60 p-3 max-h-44 overflow-y-auto" data-testid="battle-log">
              {(battle.log || []).slice(-6).map((e, i) => (
                <LogLine key={`${e.turn}-${i}-${e.skill_id || e.message?.slice(0,8)}`} entry={e} />
              ))}
            </div>

            {/* Player card */}
            <CombatantCard
              actor={battle.player}
              side="player"
              isTurn={isPlayerTurn}
              statuses={battle.player_statuses || []}
              role={playerRole}
              effect={effect}
              nameplate={battle.player_nameplate}
              identityColor={battle.player_identity_color}
            />

            {/* Terminal state banner */}
            {isOver && (
              <TerminalBanner
                status={battle.status}
                rewards={battle.rewards}
                returnTo={returnTo}
                onStart={onStart}
                onReturn={() => navigate(returnTo)}
                onViewHighlight={() => setShowReel(true)}
                busy={busy}
              />
            )}

            {/* 🎬 Highlight Reel (victories only) */}
            {showReel && battle?.status === "won" && (
              <HighlightReelModal
                battle={battle}
                onClose={() => setShowReel(false)}
              />
            )}

            {/* Skill picker */}
            {!isOver && isPlayerTurn && (
              <div className="flex items-start gap-2" data-testid="battle-action-bar">
                <div className="flex-1 min-w-0">
                  <SkillPicker
                    player={battle.player}
                    onSubmit={onSubmitMove}
                    busy={busy}
                  />
                </div>
                {/* 🎒 Battle Bag — Mar 5, 2026 */}
                <BattleBagButton disabled={busy} onUse={onUseItem} />
              </div>
            )}

            {/* 🤝 Combo Ultimate bar */}
            {!isOver && isPlayerTurn && (
              <ComboUltimateBar
                player={battle.player}
                cooldowns={battle.combo_cooldowns || {}}
                onFire={onFireCombo}
                busy={busy}
              />
            )}

            {/* Forfeit */}
            {!isOver && (
              <div className="mt-4 text-center">
                <button
                  onClick={onForfeit}
                  disabled={busy}
                  data-testid="battle-forfeit-btn"
                  className="text-[11px] text-neutral-500 hover:text-red-400 underline italic"
                >
                  Withdraw from the fight
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────
 * 🎞️ Effect derivation from a log entry
 * Inputs come from manual_battle_service:
 *   { actor: 'player'|'enemy'|'system', kind, damage, is_crit,
 *     type_label, healed, absorbed, applied_statuses[],
 *     pressing_on_doubled, status_modifiers[] }
 * Output drives card transforms + floating numbers + flash colour.
 * ────────────────────────────────────────────────────────────── */
function deriveEffectFromLogEntry(e) {
  if (!e || e.actor === "system") {
    // 🌀 System lines = status engine drains / expiries → soft target flash
    if (e?.message?.includes("Soul Eater") || e?.message?.includes("drinks")) {
      // We don't know which side from the message alone — skip flash.
      return null;
    }
    return null;
  }
  const attacker = e.actor === "player" ? "player" : "enemy";
  const isAtk = ["atk_p", "atk_m", "atk_true", "ultimate", "leech"].includes(e.kind);
  const isSelf = ["heal", "absorb"].includes(e.kind);
  const target = isSelf ? attacker : (attacker === "player" ? "enemy" : "player");

  const vfx = [];
  if (e.is_crit) vfx.push("crit");
  if (e.type_label === "true") vfx.push("true");
  if (e.type_label === "super_effective") vfx.push("super");
  if (e.type_label === "resisted") vfx.push("resisted");
  if (e.kind === "heal") vfx.push("heal");
  if (e.kind === "absorb") vfx.push("absorb");
  if (e.kind === "leech") vfx.push("leech");
  if (e.kind === "ultimate") vfx.push("ultimate");
  if (e.pressing_on_doubled) vfx.push("doubled");
  if ((e.applied_statuses || []).length > 0) vfx.push("status_applied");

  return {
    attacker,
    target,
    isAtk,
    isSelf,
    kind: e.kind,
    vfx,
    damage: e.damage || 0,
    healed: e.healed || 0,
    absorbed: e.absorbed || 0,
    appliedStatusId: (e.applied_statuses || [])[0] || null,
  };
}

function roleForSide(effect, side) {
  if (!effect) return null;
  if (effect.attacker === side && !effect.isSelf) return "attacker";
  if (effect.target === side) return effect.isSelf ? "target_self" : "target";
  return null;
}



/* ──────────────────────────────────────────────────────────────
 * 🦅 Combatant card — now with animation transform + status chips +
 * floating-number overlay.
 * ────────────────────────────────────────────────────────────── */
function CombatantCard({ actor, side, isTurn, statuses, role, effect, nameplate, identityColor }) {
  if (!actor) return null;
  const hpPct = Math.max(0, Math.min(100, Math.round((actor.hp / actor.max_hp) * 100)));
  const isEnemy = side === "enemy";
  const cls = isEnemy
    ? "border-red-700/50 bg-gradient-to-br from-red-950/40 to-black"
    : "border-amber-700/50 bg-gradient-to-br from-amber-950/30 to-black";

  // 🎞️ Animation class — derived from role + vfx
  const animClass = animClassFor(role, effect, isEnemy);
  // Hit flash overlay — only on target side (or self for heal/absorb)
  const flashClass = flashClassFor(role, effect);

  // 🎨 Identity color outline (Mar 5, 2026). Applied to the player card
  // only — enemies keep the red battle theming. Suppressed while the card
  // is actively animating a hit so the flash overlay isn't muted.
  const idStyle = (!isEnemy && identityColor && !flashClass)
    ? {
        borderColor: identityColor.hex,
        boxShadow: identityColor.hex_secondary
          ? `0 0 16px ${identityColor.glow}, inset 0 0 12px ${identityColor.glow_secondary}`
          : `0 0 12px ${identityColor.glow}`,
      }
    : undefined;

  return (
    <div
      className={`relative rounded-2xl border-2 ${cls} p-4 overflow-visible ${animClass}`}
      style={idStyle}
      data-testid={`combatant-${side}`}
      data-anim-role={role || "idle"}
    >
      {/* 💥 Hit-flash overlay */}
      {flashClass && (
        <div
          className={`pointer-events-none absolute inset-0 rounded-2xl ${flashClass}`}
          data-testid={`flash-${side}`}
        />
      )}

      {/* 🔢 Floating number — appears on target (damage) / self (heal·absorb) */}
      <FloatingNumber side={side} role={role} effect={effect} />

      {/* 🖼️ Sprite portrait + VFX overlay */}
      <SpriteFighter actor={actor} side={side} role={role} effect={effect} isEnemy={isEnemy} />

      <div className="relative flex items-center justify-between gap-3 mt-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* 🐺 Feb 2026 — When fighting a pack, show one icon per
              member so a 4-pack visually reads as 4 enemies, not one.
              Dead members dim out. Owner directive: "4 icons + segmented
              HP bar + log line like 'defeated 2 of 4'". */}
          {Array.isArray(actor.pack_members) && actor.pack_members.length > 1 ? (
            <div className="flex -space-x-2 shrink-0" data-testid={`${side}-pack-icons`}>
              {actor.pack_members.map((m, idx) => (
                <div
                  key={idx}
                  className={`text-2xl leading-none transition-all duration-500 ${
                    m.alive ? "opacity-100" : "opacity-20 grayscale saturate-0"
                  }`}
                  data-testid={`${side}-pack-member-${idx}${m.alive ? "" : "-dead"}`}
                  title={m.alive ? `Member ${idx + 1} · ${m.hp}/${m.max_hp} HP` : `Member ${idx + 1} · defeated`}
                >
                  {actor.icon || (isEnemy ? "🎯" : "🦅")}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-2xl shrink-0" data-testid={`${side}-icon`}>
              {actor.icon || (isEnemy ? "🎯" : "🦅")}
            </div>
          )}
          <div className="min-w-0">
            {/* 🎭 Mar 4, 2026 owner drop — Hero nameplate on top when
                authored (e.g. "Sloane — The Quiet Mend 🐢🌸"), player
                username on line 2. Enemies keep the single-line layout. */}
            {nameplate ? (
              <>
                <div
                  className="text-base font-black text-amber-200 truncate leading-tight"
                  data-testid={`${side}-nameplate`}
                  title={nameplate}
                >
                  {nameplate}
                </div>
                <div
                  className="text-[11px] text-neutral-400 truncate leading-tight"
                  data-testid={`${side}-username`}
                >
                  @{actor.name}
                </div>
              </>
            ) : (
              <div className="text-base font-black text-amber-200 truncate" data-testid={`${side}-name`}>
                {actor.name}
              </div>
            )}
            <div className="text-[10px] text-neutral-500 uppercase tracking-widest">
              {isEnemy ? actor.kind : (actor.class_id || "no class")} · Lv {actor.level}
            </div>
          </div>
        </div>
        {isTurn && (
          <div className="px-2 py-0.5 rounded-full bg-amber-500 text-black text-[10px] font-black animate-pulse">
            ⚡ ACTING
          </div>
        )}
      </div>

      {/* 🌀 Status chip bar */}
      {statuses && statuses.length > 0 && (
        <StatusChipBar statuses={statuses} side={side} />
      )}

      {/* HP bar — segmented when pack */}
      <div className="relative mt-3">
        <div className="flex justify-between text-[10px] text-neutral-400 mb-1">
          <span>HP{Array.isArray(actor.pack_members) && actor.pack_members.length > 1 ? ` (${actor.pack_members.filter(m => m.alive).length} / ${actor.pack_members.length})` : ""}</span>
          <span data-testid={`${side}-hp`}>{actor.hp} / {actor.max_hp}</span>
        </div>
        {Array.isArray(actor.pack_members) && actor.pack_members.length > 1 ? (
          <div
            className="flex gap-0.5 h-3"
            data-testid={`${side}-hp-segmented`}
          >
            {actor.pack_members.map((m, idx) => {
              const mp = m.max_hp > 0 ? (m.hp / m.max_hp) * 100 : 0;
              return (
                <div key={idx} className="flex-1 rounded bg-black/60 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      !m.alive ? "bg-neutral-800" :
                      mp > 50 ? "bg-emerald-500" :
                      mp > 25 ? "bg-amber-500" : "bg-red-500"
                    }`}
                    style={{ width: `${Math.max(0, Math.min(100, mp))}%` }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-3 rounded bg-black/60 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                hpPct > 50 ? "bg-emerald-500" : hpPct > 25 ? "bg-amber-500" : "bg-red-500"
              }`}
              style={{ width: `${hpPct}%` }}
            />
          </div>
        )}
      </div>

      {/* Mana bar (player only) */}
      {!isEnemy && actor.max_mana > 0 && (
        <div className="mt-2">
          <div className="flex justify-between text-[10px] text-neutral-400 mb-1">
            <span>MANA</span>
            <span data-testid="player-mana">{actor.mana} / {actor.max_mana}</span>
          </div>
          <div className="h-2 rounded bg-black/60 overflow-hidden">
            <div className="h-full bg-sky-500 transition-all" style={{ width: `${Math.round((actor.mana/actor.max_mana)*100)}%` }} />
          </div>
        </div>
      )}

      {/* 🛡️ Absorb shield indicator (player only, when active) */}
      {!isEnemy && actor.absorb_shield > 0 && (
        <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-900/40 border border-cyan-500/50">
          <span className="text-[10px] text-cyan-200 font-mono">🛡️ {actor.absorb_shield} absorb</span>
        </div>
      )}
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────
 * 🌀 Status chip bar — one chip per active status with duration pip
 * ────────────────────────────────────────────────────────────── */
function StatusChipBar({ statuses, side }) {
  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-1.5"
      data-testid={`status-chips-${side}`}
    >
      {statuses.map((s, i) => {
        const tint = chipTintFor(s);
        const dur = s.duration == null ? "∞" : `${s.duration}t`;
        const stacks = (s.stacks || 1) > 1 ? `×${s.stacks}` : "";
        return (
          <div
            key={`${s.id}-${i}`}
            className={`relative inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tint} status-chip-ping`}
            title={`${s.label} · ${dur}${stacks ? " · " + stacks : ""}`}
            data-testid={`status-chip-${side}-${s.id}`}
          >
            <span className="text-[11px] font-black">{s.label}</span>
            <span className="text-[9px] font-mono opacity-70">{dur}{stacks ? ` ${stacks}` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

function chipTintFor(s) {
  // Canon palette: bleeds rose, drains amber, denial slate, buffs emerald, stances cyan
  if (s.id === "haemorrhage") return "border-rose-500/60 bg-rose-900/40 text-rose-100";
  if (s.id === "heal_block") return "border-slate-400/60 bg-slate-800/60 text-slate-100";
  if (s.id === "soul_eater_drain") return "border-amber-500/60 bg-amber-900/40 text-amber-100";
  if (s.kind === "buff") return "border-emerald-500/60 bg-emerald-900/40 text-emerald-100";
  if (s.kind === "drain" || s.kind === "dot") return "border-orange-500/60 bg-orange-900/40 text-orange-100";
  if (s.kind === "stance") return "border-cyan-500/60 bg-cyan-900/40 text-cyan-100";
  return "border-purple-500/60 bg-purple-900/40 text-purple-100";
}


/* ──────────────────────────────────────────────────────────────
 * 🔢 Floating damage/heal number — anchored to a combatant card
 * ────────────────────────────────────────────────────────────── */
function FloatingNumber({ side, role, effect }) {
  if (!effect || !role) return null;
  // Show damage on target sides; heal/absorb on self sides.
  let label = null;
  let tint = "text-amber-200";
  if (role === "target") {
    if (effect.damage > 0) {
      const isCrit = effect.vfx.includes("crit");
      const isTrue = effect.vfx.includes("true");
      label = `-${effect.damage}`;
      tint = isTrue
        ? "text-fuchsia-300 drop-shadow-[0_0_8px_rgba(232,121,249,0.8)]"
        : isCrit
          ? "text-amber-300 drop-shadow-[0_0_10px_rgba(252,211,77,0.9)]"
          : "text-red-300 drop-shadow-[0_0_6px_rgba(248,113,113,0.6)]";
    }
  } else if (role === "target_self") {
    if (effect.kind === "heal" && effect.healed > 0) {
      label = `+${effect.healed}`;
      tint = "text-emerald-300 drop-shadow-[0_0_8px_rgba(110,231,183,0.8)]";
    } else if (effect.kind === "absorb" && effect.absorbed > 0) {
      label = `🛡️ ${effect.absorbed}`;
      tint = "text-cyan-300 drop-shadow-[0_0_6px_rgba(103,232,249,0.7)]";
    }
  }
  if (!label) return null;
  return (
    <div
      key={effect.key}
      className={`pointer-events-none absolute right-4 top-2 text-3xl font-black ${tint} dmg-float`}
      data-testid={`floating-num-${side}`}
    >
      {label}
      {effect.vfx.includes("crit") && <span className="ml-1 text-sm align-top">CRIT!</span>}
      {effect.vfx.includes("true") && <span className="ml-1 text-sm align-top">TRUE</span>}
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────
 * 📜 Rich combat-log line — kind-aware highlighting
 * ────────────────────────────────────────────────────────────── */
function LogLine({ entry: e }) {
  const baseTint =
    e.actor === "player" ? "text-amber-200" :
    e.actor === "enemy" ? "text-red-300" :
    "text-purple-300/80 italic";

  // Build accent badges from entry data
  const badges = [];
  if (e.is_crit) badges.push({ k: "crit", label: "💥 CRIT", cls: "bg-amber-500/30 text-amber-100 border-amber-400/60" });
  if (e.type_label === "true") badges.push({ k: "true", label: "TRUE", cls: "bg-fuchsia-700/40 text-fuchsia-100 border-fuchsia-400/60" });
  if (e.type_label === "super_effective") badges.push({ k: "super", label: "SUPER", cls: "bg-orange-700/40 text-orange-100 border-orange-400/60" });
  if (e.type_label === "resisted") badges.push({ k: "resist", label: "RESIST", cls: "bg-slate-700/50 text-slate-100 border-slate-400/50" });
  if (e.kind === "heal") badges.push({ k: "heal", label: "HEAL", cls: "bg-emerald-700/40 text-emerald-100 border-emerald-400/60" });
  if (e.kind === "absorb") badges.push({ k: "absorb", label: "SHIELD", cls: "bg-cyan-700/40 text-cyan-100 border-cyan-400/60" });
  if (e.kind === "leech") badges.push({ k: "leech", label: "LEECH", cls: "bg-rose-700/40 text-rose-100 border-rose-400/60" });
  if (e.kind === "ultimate") badges.push({ k: "ult", label: "ULT", cls: "bg-purple-700/40 text-purple-100 border-purple-400/60" });
  if (e.pressing_on_doubled) badges.push({ k: "doubled", label: "×2", cls: "bg-pink-700/40 text-pink-100 border-pink-400/60" });
  if ((e.applied_statuses || []).length > 0) {
    badges.push({ k: "status", label: "🌀 STATUS", cls: "bg-indigo-700/40 text-indigo-100 border-indigo-400/60" });
  }

  return (
    <div
      className={`text-[12px] font-mono leading-relaxed flex items-start gap-2 py-0.5 ${baseTint}`}
      data-testid={`log-line-t${e.turn}`}
      data-log-kind={e.kind || "system"}
    >
      <span className="text-neutral-500 shrink-0">[T{e.turn}]</span>
      <div className="flex-1 min-w-0">
        <span>{e.message}</span>
        {badges.length > 0 && (
          <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">
            {badges.map((b) => (
              <span
                key={b.k}
                className={`px-1.5 py-0 rounded border text-[9px] font-black tracking-wider ${b.cls}`}
                data-testid={`log-badge-${b.k}`}
              >
                {b.label}
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}



/* ──────────────────────────────────────────────────────────────
 * 🤝 Combo Ultimate Bar — surfaces every combo the player is a party
 * to. Cards dim if on cooldown. Fires POST /api/manual-battle/combo-ult.
 * ────────────────────────────────────────────────────────────── */
function ComboUltimateBar({ player, cooldowns, onFire, busy }) {
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const classId = player?.class_id;

  useEffect(() => {
    if (!classId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/combo-ultimates`);
        if (cancelled) return;
        // Show only combos where THIS class is one of the two partners
        // AND the combo is authored (skip drafts).
        const mine = (data.items || []).filter((c) =>
          (c.main_class_id === classId || c.partner_class_id === classId)
          && String(c.status || "").startsWith("✅")
        );
        setCombos(mine);
      } catch { /* silent — the bar just hides */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [classId]);

  if (loading || combos.length === 0) return null;

  return (
    <div className="mt-4 border border-amber-900/40 bg-black/40 p-3" data-testid="combo-ult-bar">
      <div className="text-[10px] uppercase tracking-widest text-amber-400/80 mb-2 flex items-center gap-2">
        <span>🤝</span>
        <span>Combo Ultimates</span>
        <span className="text-neutral-600 normal-case tracking-normal italic ml-2">
          Invoke your partner cameo-style. Un-blockable, un-evadable.
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {combos.map((c) => {
          const cd = Number(cooldowns?.[c.combo_id] || 0);
          const onCd = cd > 0;
          // 🩹 Oct 2026 (Fix #13) — Combo ultimates must gate at ≥80 mana
          // on the trigger hero. Backend catalogs sometimes ship mana_cost=0
          // which previously let "0 MANA · READY" show up at empty mana.
          // Enforce the design floor client-side so the UI never contradicts
          // the gate — backend also rejects the fire if under 80.
          const COMBO_MANA_FLOOR = 80;
          const effectiveCost = Math.max(Number(c.mana_cost || 0), COMBO_MANA_FLOOR);
          const notEnoughMana = effectiveCost > Number(player.mana || 0);
          const disabled = busy || onCd || notEnoughMana;
          return (
            <button
              key={c.combo_id}
              type="button"
              data-testid={`combo-fire-${c.combo_id}`}
              disabled={disabled}
              onClick={() => onFire(c.combo_id)}
              className={`text-left border p-2.5 transition-colors ${
                disabled
                  ? "border-zinc-800 bg-black/40 opacity-50 cursor-not-allowed"
                  : "border-amber-500/70 hover:border-amber-400 hover:bg-amber-500/10 shadow-[0_0_12px_rgba(212,175,55,0.15)]"
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="text-2xl leading-none">{c.icon || "🤝"}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-amber-200">{c.name}</div>
                  <div className="text-[10px] text-neutral-500 truncate">
                    with {c.partner_nameplate?.split("—")[0]?.trim() || c.partner_hero_id}
                  </div>
                  <div className="flex gap-2 mt-1 text-[9px] uppercase tracking-widest">
                    <span className="text-neutral-400 border border-zinc-800 px-1 py-0.5">
                      ⚡ {effectiveCost} mana
                    </span>
                    {onCd ? (
                      <span className="text-red-400 border border-red-900 px-1 py-0.5">
                        CD {cd}t
                      </span>
                    ) : notEnoughMana ? (
                      <span className="text-neutral-500 border border-zinc-800 px-1 py-0.5">
                        Low mana
                      </span>
                    ) : (
                      <span className="text-emerald-400 border border-emerald-900/60 px-1 py-0.5">
                        Ready
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────
 * Skill picker (unchanged behaviour)
 * ────────────────────────────────────────────────────────────── */
function SkillPicker({ player, onSubmit, busy }) {
  // 💠 Owner canon Feb 2026: every hero's skill[0] is their Generator, and
  // Generators are ALWAYS free-cast + zero cooldown. We detect Generators
  // two ways (belt + suspenders):
  //   1. `mana_gain > 0` (explicit tag — covers 21/23 heroes)
  //   2. skill is at index 0 in the hero's kit (universal identity rule —
  //      covers Cassandra's placeholder + Mother Phoenix's signature)
  const firstSkillId = (player.skills || []).find(s => s.kind !== "trigger")?.id;
  const usability = (skill) => {
    const basic = ["basic_strike", "basic_bolt"].includes(skill.id);
    const isGenerator =
      Number(skill?.mana_gain || 0) > 0 ||
      Number(skill?.bird_cracker_gain || 0) > 0 ||
      Number(skill?.scrap_gain || 0) > 0 ||
      Number(skill?.blueprint_scrap_gain || 0) > 0 ||
      skill.id === firstSkillId;
    // 🎯 Mar 2026 canon (ULTIMATE_MANA_COST_SCHEDULE): read authoritative
    // `mana_cost` first. Fall back to the tier schedule only if the skill
    // doesn't declare one. Lv 1–2 ults → 80💠 · Lv 3–4 ults → 100💠 ·
    // active → 25💠 · generator → 0💠.
    const cost = (() => {
      if (basic || isGenerator) return 0;
      const declared = Number(skill?.mana_cost || 0);
      if (declared > 0) return declared;
      if (skill.kind === "ultimate") {
        const lvl = Number(skill?.ult_level || 0);
        if (lvl === 3 || lvl === 4) return 100;
        return 80;
      }
      if (["heal", "absorb", "leech", "atk_p", "atk_m", "atk_true"].includes(skill.kind)) return 25;
      return 0;
    })();
    const cd = (player.cooldowns || {})[skill.id] || 0;
    // Generators never have a cooldown (backend contract) — skip the CD gate.
    if (!isGenerator && cd > 0) return { ok: false, reason: `Cooldown ${cd}t` };
    if (player.mana < cost) return { ok: false, reason: `Need ${cost} mana` };
    if (player.level < (skill.lvl_req || 1)) return { ok: false, reason: `Lv ${skill.lvl_req}` };
    return { ok: true, reason: cost === 0 ? `Free · ${skill.kind}` : `Cost ${cost} · ${skill.kind}` };
  };
  // 🚫 Combo Ults (Tier 3 · Duo) belong exclusively to the ComboUltimatesBar
  // rendered BELOW the SkillPicker. Filter them out of the main move list.
  // Detection: explicit `ult_tier === "combo"` tag, OR presence of
  // `combo_requires`, OR `dormant: true` (combo/skill-not-active flag).
  const playable = (player.skills || []).filter((s) => {
    if (s.kind === "trigger") return false;
    if (s.ult_tier === "combo") return false;
    if (Array.isArray(s.combo_requires) && s.combo_requires.length > 0) return false;
    if (s.dormant === true) return false;
    return true;
  });
  return (
    <div className="mt-4">
      <div className="text-[10px] uppercase tracking-widest text-amber-300/70 mb-2">⚡ Your move</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="skill-picker">
        {playable.map((s) => {
          const u = usability(s);
          const isUlt = s.kind === "ultimate";
          const isHeal = s.kind === "heal";
          const isAbsorb = s.kind === "absorb";
          const isLeech = s.kind === "leech";
          const tint = !u.ok
            ? "border-neutral-800 bg-neutral-900/40 opacity-60 cursor-not-allowed"
            : isUlt
              ? "border-purple-600 bg-gradient-to-br from-purple-900/40 to-black hover:border-purple-400 hover:shadow-[0_0_20px_rgba(168,85,247,0.3)]"
              : isHeal
                ? "border-emerald-700 bg-gradient-to-br from-emerald-900/30 to-black hover:border-emerald-400"
                : isAbsorb
                  ? "border-cyan-700 bg-gradient-to-br from-cyan-900/30 to-black hover:border-cyan-400"
                  : isLeech
                    ? "border-rose-800 bg-gradient-to-br from-rose-950/40 to-black hover:border-rose-500"
                    : "border-amber-700 bg-gradient-to-br from-amber-900/20 to-black hover:border-amber-400";
          const kindLabel = isUlt ? "Ult"
            : isAbsorb ? "Absorb"
            : isLeech ? "Leech"
            : isHeal ? "Heal"
            : null;
          const kindBadgeTint = isUlt ? "text-purple-300"
            : isAbsorb ? "text-cyan-300"
            : isLeech ? "text-rose-300"
            : isHeal ? "text-emerald-300"
            : "text-amber-300";
          return (
            <button
              key={s.id}
              onClick={() => u.ok && onSubmit(s.id)}
              disabled={busy || !u.ok}
              data-testid={`skill-btn-${s.id}`}
              className={`rounded-xl border-2 p-3 text-left transition ${tint}`}
            >
              <div className="flex items-start gap-3">
                <div className="text-3xl">{s.icon || "✨"}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-black text-amber-100 truncate">{s.name}</div>
                    <div className="flex items-center gap-1">
                      {s.random_target && (
                        <span
                          className="text-[9px] font-black uppercase tracking-widest text-cyan-300 bg-cyan-950/50 px-1 py-0.5 rounded"
                          data-testid={`skill-random-target-${s.id}`}
                          title="Auto-targets a random enemy"
                        >🎲</span>
                      )}
                      {kindLabel && (
                        <span className={`text-[9px] font-black uppercase tracking-widest ${kindBadgeTint}`} data-testid={`skill-badge-${s.id}`}>{kindLabel}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-[11px] text-amber-100/70 mt-0.5 line-clamp-2">{s.description}</div>
                  <div className={`text-[10px] mt-1 font-mono ${u.ok ? "text-amber-300/70" : "text-red-400/80"}`}>
                    {u.reason}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function TerminalBanner({ status, rewards, returnTo, onStart, onReturn, onViewHighlight, busy }) {
  const isWin = status === "won";
  const isLoss = status === "lost";
  const isRaid = (rewards?.context === "castle_raid");
  const cls = isWin
    ? "border-amber-500/70 bg-amber-900/20"
    : isLoss
      ? "border-red-700/70 bg-red-900/20"
      : "border-neutral-700 bg-neutral-900/30";
  const icon = isRaid
    ? (isWin ? "🏰⚔️" : isLoss ? "🏰🛡️" : "🏳️")
    : (isWin ? "🏆" : isLoss ? "💀" : "🏳️");
  const title = isRaid
    ? (isWin ? "Raid Successful" : isLoss ? "Raid Repelled" : "Withdrew")
    : (isWin ? "Victory" : isLoss ? "Defeat" : "Withdrew");
  const msg = isRaid
    ? (isWin
        ? `You breached the keep and made off with the spoils.`
        : isLoss
          ? `The keep held. You were driven out empty-handed.`
          : `You stepped back. The keep still stands.`)
    : (isWin
        ? "You held the line. The fight is yours."
        : isLoss
          ? "It got you. Re-read the log — what would you change?"
          : "You stepped back. The enemy is still standing.");
  return (
    <div className={`mt-4 rounded-2xl border-2 ${cls} p-5 text-center`} data-testid={`terminal-${status}`}>
      <div className="text-6xl mb-2">{icon}</div>
      <div className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
        {title}
      </div>
      <p className="text-sm text-amber-100/80 mt-1 italic">{msg}</p>

      {/* 🏰 Castle Raid Report — always shown on a raid, win OR loss. */}
      {isRaid && rewards && (
        <RaidReport rewards={rewards} won={isWin} />
      )}

      {/* 💰 Generic spoils (non-raid Wilds/PvE wins) */}
      {!isRaid && isWin && rewards && (
        <div className="mt-4 rounded-lg border border-amber-500/40 bg-black/40 p-3" data-testid="battle-rewards">
          <div className="text-[10px] text-amber-300/80 font-black uppercase tracking-widest mb-2">💰 Spoils</div>
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
            {rewards.chits != null && (
              <span className="text-amber-200">🪙 <b>{rewards.chits}</b> chits</span>
            )}
            {rewards.pet_xp != null && rewards.pet_xp > 0 && (
              <span className="text-emerald-300">✨ <b>+{rewards.pet_xp}</b> pet XP</span>
            )}
            {rewards.gear && (
              <span className="text-purple-300">🎁 {rewards.gear.name}</span>
            )}
            {rewards.material && (
              <span className="text-sky-300">{rewards.material.icon} {rewards.material.name}</span>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {isWin && onViewHighlight && (
          <button
            onClick={onViewHighlight}
            disabled={busy}
            data-testid="terminal-view-highlight-btn"
            className="px-5 py-2 rounded bg-gradient-to-r from-purple-500 to-amber-500 hover:from-purple-400 hover:to-amber-400 disabled:opacity-50 text-black font-black text-sm shadow-lg shadow-amber-500/30"
          >
            🎬 View Highlight
          </button>
        )}
        <button
          onClick={onStart}
          disabled={busy}
          data-testid="terminal-restart-btn"
          className="px-5 py-2 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-sm"
        >
          ⚔️ Fight Again
        </button>
        {returnTo && (
          <button
            onClick={onReturn}
            disabled={busy}
            data-testid="terminal-return-btn"
            className="px-5 py-2 rounded border border-amber-600/60 hover:bg-amber-900/30 text-amber-200 font-black text-sm"
          >
            ↩ Return
          </button>
        )}
      </div>
    </div>
  );
}

// 🏰 Castle Raid Report — always-on summary of what was won / not won.
// Owner spec (Feb 2026): "add what was raided/stolen and how much was taken
// in win/loose and report." Render on both outcomes; on loss, explicitly
// surface that nothing was stolen + show daily counter so the player knows
// the raid still counted.
function RaidReport({ rewards, won }) {
  const r = rewards || {};
  const defName = r.defender_name || "the castle";
  const lvl = r.defender_level;
  const chits = r.chits_won || 0;
  const spirit = r.spirit_gifted || 0;
  const trophies = r.trophies_won || 0;
  const raidsToday = r.raids_today;
  const dailyCap = r.daily_cap;
  const isNpc = !!r.defender_is_npc;

  return (
    <div
      className={`mt-4 rounded-lg border bg-black/50 p-4 text-left ${
        won ? "border-amber-500/50" : "border-red-700/50"
      }`}
      data-testid="raid-report"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-black uppercase tracking-widest text-amber-300/80">
          🏰 Raid Report
        </div>
        <div className="text-[10px] text-neutral-500">
          {isNpc && <span className="mr-2 text-fuchsia-300">🤖 NPC</span>}
          {lvl != null && <span>Lv {lvl}</span>}
        </div>
      </div>

      <div className="text-sm text-amber-100/90 mb-3">
        Target: <span className="font-bold text-amber-200">{defName}</span>
      </div>

      {/* Loot grid — green on win, dim on loss with explicit zeroes */}
      <div className="grid grid-cols-3 gap-2">
        <RaidLootCell
          icon="🪙"
          label="Chits Stolen"
          value={chits}
          gained={won && chits > 0}
          testid="raid-report-chits"
        />
        <RaidLootCell
          icon="🥊"
          label="Brawler Spirit"
          value={spirit}
          gained={won && spirit > 0}
          testid="raid-report-spirit"
        />
        <RaidLootCell
          icon="🏆"
          label="Trophies"
          value={trophies}
          gained={won && trophies > 0}
          testid="raid-report-trophies"
        />
      </div>

      {/* Loss explainer — owner wants this to be SEEN on losses */}
      {!won && (
        <div className="mt-3 rounded border border-red-800/50 bg-red-950/30 px-3 py-2 text-xs text-red-200" data-testid="raid-report-loss-note">
          <b>Nothing was stolen.</b> The keep held — no Chits, Spirit, or Trophies leave the vault on a loss. Try again with a stronger party or a different target.
        </div>
      )}

      {/* Win explainer — defender bubble */}
      {won && (
        <div className="mt-3 rounded border border-sky-800/50 bg-sky-950/30 px-3 py-2 text-xs text-sky-200 flex items-center gap-2" data-testid="raid-report-bubble-note">
          <span>🛡️</span>
          <span>
            <b>{defName}</b> is sealed in a <b>2-hour Protection Bubble</b> — they can&apos;t be raided again until it lifts.
          </span>
        </div>
      )}

      {/* Daily counter — always visible (the raid counted regardless of outcome) */}
      {raidsToday != null && dailyCap != null && (
        <div className="mt-3 text-[11px] text-neutral-400 text-center" data-testid="raid-report-counter">
          🎯 Raid attempt counted: <span className="text-amber-200 font-bold">{raidsToday}</span>/{dailyCap} today
        </div>
      )}
    </div>
  );
}

function RaidLootCell({ icon, label, value, gained, testid }) {
  return (
    <div
      className={`rounded border px-2 py-2 text-center transition ${
        gained ? "border-emerald-700/50 bg-emerald-950/30"
               : "border-neutral-800 bg-black/50 opacity-60"
      }`}
      data-testid={testid}
    >
      <div className="text-2xl leading-none mb-1">{icon}</div>
      <div
        className={`text-lg font-black tabular-nums leading-none mb-1 ${
          gained ? "text-emerald-200" : "text-neutral-500"
        }`}
      >
        {gained ? "+" : ""}{(value || 0).toLocaleString()}
      </div>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}
